import { memo, useCallback, useEffect, useMemo, useState } from 'react';
// Types
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { get } from 'lodash';
// Slices
import {
  checkFirstDrawerExist,
  createUnsavedPeriod,
  deletePeriod,
  duplicatePeriod,
  periodsLoading,
  periodsMapSelector,
  periodsSelector,
  selectedIdsSelector,
  setFirstDrawer,
  setSecondDrawer,
} from 'slices/period.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
// Utils
import { matchString } from 'utils/stringUtils';
import { EPeriodAccess, type TPeriod } from '@timeedit/types';
// Components
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
  type TEIOption,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
import { convertTypeToString } from './sections/Intervals';

export interface IRowData {
  id: number;
  name: string;
  type: string;
}

interface Props {
  confirmDiscardChanges: (isFirstDrawer: boolean, actionFn: () => void) => void;
}

const PeriodsList = ({ confirmDiscardChanges }: Props) => {
  const dispatch = useAppDispatch();

  const periodsData: TPeriod[] = useAppSelector(periodsSelector);
  const periodsMap: Record<number, TPeriod> =
    useAppSelector(periodsMapSelector);
  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const selectedIds: number[] = useAppSelector(selectedIdsSelector);

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(selectedIds);
    }, 100);
  }, [selectedIds.join('-')]);

  const loading = useAppSelector(periodsLoading);

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const filterData: any[] = useMemo(() => {
    const dataSource = periodsData?.map((period) => ({
      ...period,
      id: period.id,
      name: period.name || 'New period',
    }));

    return dataSource.filter((pr) => {
      let matchedType = true;
      let matchedText = true;
      const {
        filters: { type },
        freeTextSearch,
      } = filterSettings;
      if (type) {
        matchedType = pr.userAccess === type;
      }
      if (freeTextSearch) {
        matchedText = matchString(pr.name, freeTextSearch);
      }
      return matchedType && matchedText;
    });
  }, [periodsData, filterSettings]);

  const onCreate = useCallback(() => {
    confirmDiscardChanges(true, () => dispatch(createUnsavedPeriod()));
  }, []);

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardChanges(true, () =>
        dispatch(duplicatePeriod(periodsMap[record.id]))
      );
    },
    [periodsMap]
  );

  const onCompare = useCallback(
    (record: IRowData) => {
      const firstDrawerExist = dispatch(checkFirstDrawerExist());
      if (!firstDrawerExist) {
        return dispatch(setFirstDrawer(periodsMap[record.id]));
      }
      confirmDiscardChanges(false, () =>
        dispatch(setSecondDrawer(periodsMap[record.id]))
      );
    },
    [periodsMap]
  );

  const onRowClick = useCallback(
    (record: IRowData) => {
      confirmDiscardChanges(true, () =>
        dispatch(setFirstDrawer(periodsMap[record.id]))
      );
    },
    [periodsMap]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onDelete = useCallback(
    (record: IRowData) =>
      confirmDelete(record.name, () =>
        dispatch(deletePeriod({ periodId: record.id }))
      ),
    [confirmDelete, dispatch]
  );

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(checkFirstDrawerExist())),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Button size="small" type="primary" onClick={() => onCreate()}>
          Create new
        </Button>
      </Space>
    ),
    [onCreate]
  );

  const title = useTableTitle<IRowData>({
    total: periodsData.length,
    filteredAmount: filterData.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="padding-rule-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        options={options}
        settings={filterSettings}
        onChange={setFilterSettings}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          footer={footer}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(PeriodsList);

const columns = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    ellipsis: true,
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.name.localeCompare(item2.name),
  },
  {
    key: 'intervals',
    dataIndex: 'intervals',
    title: 'Intervals',
    ellipsis: true,
    render: (intervals: any) => {
      return intervals
        ?.map(
          (interval: any) =>
            interval.type +
            ` (${convertTypeToString(
              interval.type,
              interval.start,
              interval.end
            )})`
        )
        .join(', ');
    },
  },
  {
    key: 'userAccess',
    dataIndex: 'userAccess',
    title: 'User Access Level',
    ellipsis: true,
    render: (userAccess: string) => get(mapPeriodAccess, userAccess, ''),
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.type.localeCompare(item2.type),
  },
];

const defaultFields: TEIFilterField[] = [
  {
    label: 'User Access Level',
    key: 'type',
    type: TEEFilterType.text,
  },
];

const mapPeriodAccess = {
  [EPeriodAccess.PUBLIC]: 'Public',
  [EPeriodAccess.HIDDEN]: 'Hidden',
  [EPeriodAccess.LIMITED]: 'Limited',
};

const options: Record<string, TEIOption[]> = {
  type: Object.values(EPeriodAccess)
    .map((type) => ({
      value: type,
      label: get(mapPeriodAccess, type, ''),
    }))
    .sort(),
};
